import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { availabilityAPI } from '../utils/api';
import toastr from 'toastr';

/**
 * AvailabilityPicker Component (Public)
 * 
 * Public page for candidates to select their preferred interview time slot
 * Accessed via tokenized link sent in email
 */
const AvailabilityPicker = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadRequest = useCallback(async () => {
    try {
      setLoading(true);
      const result = await availabilityAPI.getAvailabilityRequest(token);
      setRequest(result.data.request);
    } catch (error) {
      console.error('Failed to load availability request:', error);
      toastr.error(error.response?.data?.error || 'Availability request not found or expired');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadRequest();
  }, [loadRequest]);

  const handleSelectSlot = async () => {
    if (selectedSlotIndex === null) {
      toastr.error('Please select a time slot');
      return;
    }

    setSubmitting(true);
    try {
      const result = await availabilityAPI.selectTimeSlot(token, selectedSlotIndex);
      toastr.success(result.data.message || 'Time slot selected successfully!');
      
      // Redirect or show success message
      setTimeout(() => {
        // Could redirect to a thank you page
        window.location.href = '/';
      }, 3000);
    } catch (error) {
      console.error('Failed to select slot:', error);
      toastr.error(error.response?.data?.error || 'Failed to select time slot');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#F9FAFB'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #E5E7EB',
            borderTopColor: '#3B82F6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p style={{ color: '#6B7280' }}>Loading availability request...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#F9FAFB'
      }}>
        <div style={{ 
          maxWidth: '500px', 
          padding: '2rem', 
          background: 'white', 
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{ color: '#EF4444', marginBottom: '1rem' }}>Request Not Found</h2>
          <p style={{ color: '#6B7280' }}>
            This availability request may have expired or is no longer available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#F9FAFB',
      padding: '2rem 1rem'
    }}>
      <div style={{ 
        maxWidth: '700px', 
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          padding: '2rem',
          textAlign: 'center',
          color: 'white'
        }}>
          <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.75rem', fontWeight: '700' }}>
            Select Your Interview Time
          </h1>
          <p style={{ margin: 0, fontSize: '1rem', opacity: 0.9 }}>
            {request.jobPostingId?.title || 'Interview'}
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '2rem' }}>
          <p style={{ color: '#374151', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
            Please select your preferred time slot from the options below. Once selected, your interview will be confirmed automatically.
          </p>

          {/* Time Slots */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1.125rem', 
              fontWeight: '600', 
              color: '#111827',
              marginBottom: '1rem'
            }}>
              Available Time Slots:
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {request.suggestedSlots && request.suggestedSlots.map((slot, index) => {
                const start = new Date(slot.start);
                const end = new Date(slot.end);
                const isSelected = selectedSlotIndex === index;

                return (
                  <button
                    key={index}
                    onClick={() => setSelectedSlotIndex(index)}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      background: isSelected ? '#EFF6FF' : '#F9FAFB',
                      border: `2px solid ${isSelected ? '#3B82F6' : '#E5E7EB'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.target.style.background = '#F3F4F6';
                        e.target.style.borderColor = '#D1D5DB';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.target.style.background = '#F9FAFB';
                        e.target.style.borderColor = '#E5E7EB';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ 
                          fontSize: '1rem', 
                          fontWeight: '600', 
                          color: '#111827',
                          marginBottom: '0.25rem'
                        }}>
                          {start.toLocaleDateString('en-US', { 
                            weekday: 'long',
                            month: 'long', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                          {start.toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })} - {end.toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </div>
                      </div>
                      {isSelected && (
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: '#3B82F6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '0.875rem'
                        }}>
                          ✓
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSelectSlot}
            disabled={selectedSlotIndex === null || submitting}
            style={{
              width: '100%',
              padding: '1rem',
              background: selectedSlotIndex === null || submitting ? '#9CA3AF' : '#1E40AF',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: selectedSlotIndex === null || submitting ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (selectedSlotIndex !== null && !submitting) {
                e.target.style.background = '#1E3A8A';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedSlotIndex !== null && !submitting) {
                e.target.style.background = '#1E40AF';
              }
            }}
          >
            {submitting ? 'Confirming...' : 'Confirm This Time Slot'}
          </button>

          {/* Expiration Notice */}
          <p style={{ 
            marginTop: '1rem', 
            fontSize: '0.875rem', 
            color: '#6B7280',
            textAlign: 'center'
          }}>
            This request expires on {new Date(request.expiresAt).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityPicker;


